import {IBackendCaller} from "./backendCaller";
import {SortOrder} from "../shared/enums/sortOrder";
import {NoticeSortField} from "../shared/enums/noticeSortField";
import {NoticeStatus} from "../shared/enums/noticeStatus";
import NoticeItem from "../stores/domainStore/pages/models/noticeItem";

export interface NoticeArticle {
  id?: string;
  title: string;
  body?: string;
  author?: string;
  isImportant: boolean;
  isAnnouncement: boolean;
  status: NoticeStatus;
  publishDate?: Date;
  expiryDate?: Date;
}

const noticesDefaultUrl = "/api/notices";

/**
 * The interface for NoticeService.
 */
export interface INoticeService {
  /**
   * A POST call to notice API to create a new notice.
   */
  saveNotice: (noticeItem: NoticeItem) => Promise<any>;

  /**
   * A PUT call to notice API to update a notice.
   */
  updateNotice: (noticeItem: NoticeItem) => Promise<any>;

  /**
   * A GET call to notice API to get a notice.
   */
  getNotice: (id: string) => Promise<any>;

  /**
   * A GET call to notice API to get a paginated list of notices.
   */
  getNotices: (
    villageId: string,
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder
  ) => Promise<any>;

  /**
   * A GET call to notice API to get a list of notices for a list of villages within a date range.
   */
  getNoticesForVillages: (
    villageIds: string[],
    startDate: Date,
    endDate: Date
  ) => Promise<any>;

  /**
   * A DELETE call to the notice API to delete a notice by ID.
   */
  deleteNotice: (id: string) => Promise<any>;

  /**
   * Converts sort field to a NoticeSortField enum.
   * @param sortField - field to be sorted
   */
  getSortBy: (sortField?: string) => NoticeSortField;
}

/**
 * Provides various calls to internal article's notice API.
 */
export class NoticeService implements INoticeService {
  backendCaller: IBackendCaller;

  constructor(backendCaller: IBackendCaller) {
    this.backendCaller = backendCaller;
  }

  saveNotice = async (notice: NoticeItem) => {
    return await this.backendCaller.callApi(
      "POST",
      noticesDefaultUrl,
      JSON.stringify(notice.requestObject)
    );
  };

  getNotices = async (
    villageId: string,
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder
  ) => {
    if (!sortOrder) {
      sortOrder = SortOrder.Descending;
    }
    const sortBy = this.getSortBy(sortField);
    return await this.backendCaller.callApi(
      "GET",
      `${noticesDefaultUrl}?pageNumber=${pageNumber}&pageSize=${sizePerPage}&sortBy=${sortBy}&sortDescending=${
        sortOrder === SortOrder.Descending
      }&villageId=${villageId}`,
      null
    );
  };

  getNoticesForVillages = async (
    villageIds: string[],
    startDate: Date,
    endDate: Date
  ) => {
    return await this.backendCaller.callApi(
      "GET",
      `${noticesDefaultUrl}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&villageId=${villageIds.join(
        ","
      )}`,
      null
    );
  };

  updateNotice = async (notice: NoticeItem) => {
    return await this.backendCaller.callApi(
      "PUT",
      `${noticesDefaultUrl}/${notice.id}`,
      JSON.stringify(notice.requestObject)
    );
  };

  getNotice = async (id: string) => {
    return await this.backendCaller.callApi(
      "GET",
      `${noticesDefaultUrl}/${id}`,
      null
    );
  };

  getSortBy = (sortField?: string): NoticeSortField => {
    switch (sortField?.toUpperCase()) {
      case "NOTICE":
        return NoticeSortField.Notice;

      case "STATUS":
        return NoticeSortField.Status;

      case "AUTHOR":
        return NoticeSortField.Author;

      case "PUBLISHDATE":
      default:
        return NoticeSortField.PublishDate;
    }
  };

  deleteNotice = (id: string) => {
    return this.backendCaller.callApi("DELETE", `${noticesDefaultUrl}/${id}`);
  };
}
